import { useTranslation } from "react-i18next";
import { TableColumn } from "react-data-table-component";
import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, CardBody, Button } from "reactstrap";
import { withRouter, useHistory, useParams } from "react-router-dom";
import { DebounceInput } from "react-debounce-input";
import {
  ColumnHeader,
  TextColumn,
  DateColumn,
  ActionButton,
  AppTable,
} from "../../components";
import DeleteModal from "../../components/Common/DeleteModal";
import { ActionTypeEnum } from "../../enums";
import { SubjectDocumentDto } from "../../interfaces";
import { PaginateQuery } from "../../models";
import { AddEditSubjectDocumentModal } from "./AddEditSubjectDocumentModal";
import { formatFileSize } from "../../services";
import { SubjectAIModelsModal } from "./SubjectAIModelsModal";
import {
  useGetSubjectDocumentsQuery,
  useDeleteSubjectDocumentMutation,
} from "../../store/api/subjectDocumentApi";

const SubjectDocument = () => {
  //#region hooks
  const { subjectId } = useParams<any>();
  const history = useHistory();
  const { t, i18n } = useTranslation();
  //#endregion

  //#region internal state
  const initState = new PaginateQuery(10, "created_at:ASC");
  const [showModelsModal, setShowModelsModal] = useState(false);
  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>({
    ...initState,
    filter: {
      ["subjectId"]: subjectId,
    },
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [selectedDocument, setSelectedDocument] =
    useState<SubjectDocumentDto | null>();

  //#endregion

  document.title = `${t("documentTitle", { pageName: "ai" })}`;

  const {
    data: dataDocuments,
    isLoading: isLoadingDocuments,
    refetch,
  } = useGetSubjectDocumentsQuery(paginateQuery);

  const [
    deleteSubjectDocument,
    { isLoading: isLoadingDelete, error: errorDelete },
  ] = useDeleteSubjectDocumentMutation();

  const columns: TableColumn<SubjectDocumentDto>[] = [
    {
      name: <ColumnHeader title="title" />,
      cell: (row) => (
        <TextColumn text={row.title[i18n.language === "ar" ? "ar" : "en"]} />
      ),
      sortable: true,
    },
    {
      name: <ColumnHeader title="fileName" />,
      cell: (row) => (
        <TextColumn
          text={row.file?.name}
          className="text-decoration-underline cursor-pointer"
          onClick={() => {
            onEdit(row.id);
          }}
        />
      ),
      sortable: false,
    },
    {
      name: <ColumnHeader title="fileSize" />,
      cell: (row) => (
        <TextColumn text={formatFileSize(Number(row.file?.size) || 0)} />
      ),
      sortable: false,
    },
    {
      name: <ColumnHeader title="models" />,
      cell: (row) => (
        <Button
          disabled={row.models?.length === 0}
          className="btn btn-success btn-sm"
          onClick={() => onViewModels(row)}
        >
          {t("models")} ({row.models?.length})
        </Button>
      ),
      sortable: false,
    },
    {
      name: <ColumnHeader title="createdAt" />,
      cell: (row) => <DateColumn date={row.createdAt} />,
      sortable: true,
    },

    {
      name: <ColumnHeader title="action" />,
      sortable: false,
      cell: (row) => (
        <React.Fragment>
          <ActionButton
            onAction={() => onEdit(row.id)}
            actionType={ActionTypeEnum.EDIT}
          />
          &nbsp;
          <ActionButton
            onAction={() => onDelete(row.id)}
            actionType={ActionTypeEnum.DELETE}
          />
        </React.Fragment>
      ),
    },
  ];

  const getSortByColumnName = (name: string) => {
    let sortBy;
    switch (name) {
      case "title.en":
      case "title.ar":
        sortBy = 1;
        break;
      case "created_at":
        sortBy = 3;
        break;
      default:
        sortBy = 3;
        break;
    }
    return sortBy;
  };

  const [defaultSortFieldId, setDefaultSortFieldId] = useState(
    getSortByColumnName("sequence_number:ASC"),
  );
  const [defaultSortAsc, setDefaultSortAsc] = useState(false);
  const [defaultPage, setDefaultPage] = useState(1);

  //#region effects

  useEffect(() => {
    const queryParams = new URLSearchParams(history.location.search);
    let query: PaginateQuery = {
      limit: queryParams.has("limit")
        ? Number(queryParams.get("limit"))
        : paginateQuery.limit,
      page: queryParams.has("page")
        ? Number(queryParams.get("page"))
        : paginateQuery.page,
      sortBy: queryParams.has("sortBy")
        ? (queryParams.get("sortBy") as string)
        : paginateQuery.sortBy,
      search: queryParams.has("search")
        ? (queryParams.get("search") as string)
        : paginateQuery.search,
    };
    if (
      queryParams.get("sortBy") &&
      queryParams.get("sortBy")?.split(":").length &&
      queryParams.get("sortBy")?.split(":").length == 2
    ) {
      setDefaultSortAsc(
        (queryParams.get("sortBy")?.split(":")[1] as string) === "ASC",
      );
      setDefaultSortFieldId(
        getSortByColumnName(queryParams.get("sortBy")?.split(":")[0] as string),
      );
    } else {
      setDefaultSortAsc(false);
      setDefaultSortFieldId(2);
    }
    setDefaultPage(query.page);
    setPaginateQuery({ ...paginateQuery, ...query });
  }, []);

  useEffect(() => {
    if (paginateQuery !== initState) {
      const { limit, page, search, sortBy } = paginateQuery;
      let query;
      if (page) query = `?page=${page}`;
      if (limit) query += `&limit=${limit}`;
      if (search && search !== "") {
        history.replace({
          search: `&search=${search}`,
        });
      }
      if (sortBy) query += `&sortBy=${sortBy}`;
      if (search) query += `&search=${search}`;
      history.replace({ search: query });
    }
  }, [paginateQuery]);

  //#endregion

  //#region handlers

  const onAdd = () => {
    setShowAddEditModal(true);
  };

  const onEdit = (id: string) => {
    setEditingItemId(id);
    setShowAddEditModal(true);
  };

  const onDelete = (id: string) => {
    setDeletingItemId(id);
    setShowDeleteModal(true);
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteSubjectDocument(deletingItemId as string).unwrap();
      setDeletingItemId(null);
      setShowDeleteModal(false);
    } catch (err) {
      console.error(err);
    }
  };

  const onSearch = (searchText: string) => {
    setPaginateQuery({ ...paginateQuery, search: searchText });
  };

  const getSortByColumnId = (columnId: number, sortDirection: string) => {
    let sortBy;
    switch (columnId) {
      case 1:
        sortBy = "title" + "." + i18n.language;
        break;
      case 3:
        sortBy = "created_at";
        break;
      default:
        sortBy = "created_at";
        break;
    }
    return `${sortBy}:${sortDirection.toUpperCase()}`;
  };

  const onChangePage = (page: number) => {
    setPaginateQuery({ ...paginateQuery, page: page });
  };

  const onChangeRowsPerPage = (
    currentRowsPerPage: number,
    currentPage: number,
  ) => {
    setPaginateQuery({
      ...paginateQuery,
      limit: currentRowsPerPage,
      page: currentPage,
    });
  };

  const onSort = (
    column: TableColumn<SubjectDocumentDto>,
    sortDirection: string,
  ) => {
    const sortBy = getSortByColumnId(column.id as number, sortDirection);
    setPaginateQuery({
      ...paginateQuery,
      sortBy: sortBy as string,
    });
  };

  const onViewModels = (_row: SubjectDocumentDto) => {
    setSelectedDocument(_row);
    setShowModelsModal(true);
  };

  useEffect(() => {
    if (selectedDocument && dataDocuments?.data) {
      const updated = dataDocuments.data.find(
        (d: SubjectDocumentDto) => d.id === selectedDocument.id,
      );
      if (updated) setSelectedDocument(updated);
    }
  }, [dataDocuments]);

  //#endregion

  return (
    <React.Fragment>
      <Container fluid>
        <Row className="mt-2 mb-4 align-items-center">
          <Col className="col-sm-auto">
            <div>
              <h4 className="mb-0">
                {t("ai")} - {t("documents")}
              </h4>
            </div>
          </Col>
        </Row>
        <Row>
          <Col xxl={12}>
            <Card>
              <CardBody>
                <Row className="g-2">
                  <Col sm={4} className="mb-2">
                    <div className="search-box">
                      <div className="search-box">
                        <DebounceInput
                          className="form-control"
                          placeholder={`${t("searchPlaceholder")}...`}
                          minLength={2}
                          debounceTimeout={300}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                          ) => onSearch(event.target.value)}
                          value={paginateQuery.search}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </div>
                  </Col>
                  <Col className="col-sm-auto ms-auto">
                    <div className="list-grid-nav hstack gap-1">
                      <Button
                        color="success"
                        onClick={() => {
                          setPaginateQuery({ ...paginateQuery });
                          refetch();
                        }}
                      >
                        {t("refresh")}
                      </Button>
                      <Button color="success" onClick={() => onAdd()}>
                        <i className="ri-add-fill me-1 align-bottom"></i>
                        {t("addDocument")}
                      </Button>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col sm={12}>
                    <AppTable
                      columns={columns}
                      data={dataDocuments?.data || []}
                      progressPending={isLoadingDocuments}
                      onSort={onSort}
                      onChangeRowsPerPage={onChangeRowsPerPage}
                      onChangePage={onChangePage}
                      defaultSortFieldId={defaultSortFieldId}
                      paginationDefaultPage={defaultPage}
                      defaultSortAsc={defaultSortAsc}
                      paginationTotalRows={dataDocuments?.meta?.totalItems}
                    />
                  </Col>
                </Row>
                <DeleteModal
                  loading={isLoadingDelete}
                  show={showDeleteModal}
                  onDeleteClick={() => onDeleteConfirm()}
                  onCloseClick={() => {
                    setShowDeleteModal(false);
                  }}
                />
                <AddEditSubjectDocumentModal
                  subjectId={subjectId}
                  id={editingItemId}
                  show={showAddEditModal}
                  onCloseClick={() => {
                    setShowAddEditModal(false);
                    setEditingItemId(null);
                  }}
                />
                <SubjectAIModelsModal
                  loading={isLoadingDocuments}
                  subjectDocument={selectedDocument}
                  show={showModelsModal}
                  onCloseClick={() => {
                    setShowModelsModal(false);
                  }}
                  onRefresh={() => {
                    setPaginateQuery({ ...paginateQuery });
                  }}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </React.Fragment>
  );
};

export const SubjectDocumentPage = withRouter(SubjectDocument);
